import { collection, getDocs, query, where } from 'firebase/firestore';
import { useCallback, useEffect, useMemo } from 'react';
import { ArchivedStoreLog, ArchivedStoreLogsGroup, StoreLog } from '../../../apiTypes';
import { useAdmin } from '../../../contexts/admin.context';
import { db } from '../../../firebase';
import { setDoc, useCollection, useCollectionOnce } from '../../../utils/api';
import { getDayTimeRange } from '../../../utils/dates';
import getDateKey from '../../../utils/dates/getDateKey';
import getDateKeys from '../../../utils/dates/getDateKeys';
import getTimeRangeFromDateKey from '../../../utils/dates/getTimeRangeFromDateKey';

export const useGetArchivedStoreLogs = (timeRange: [number, number]) => {
  const { cafeSlug } = useAdmin();

  const tadayTimeRange = useMemo(() => getDayTimeRange(Date.now()), []);

  const { data: todayLogs } = useCollectionOnce<StoreLog>(`cafes/${cafeSlug}/storeLogs`, {
    where: ['time', '>', tadayTimeRange[0]],
    where2: ['time', '<', tadayTimeRange[1]],
  });

  const { data, isValidating } = useCollection<ArchivedStoreLogsGroup>(`cafes/${cafeSlug}/archivedStoreLogs`, {
    where: ['dateKey', '>=', getDateKey(timeRange[0])],
    where2: ['dateKey', '<=', getDateKey(timeRange[1])],
  });

  const todayDayKey = useMemo(() => getDateKey(new Date()), []);

  const dateKeys = useMemo(() => getDateKeys(timeRange), [timeRange]);

  const hasToday = useMemo(() => dateKeys.includes(todayDayKey), [dateKeys, todayDayKey]);

  const missedDateKeys = useMemo(
    () =>
      isValidating || !data
        ? []
        : dateKeys.filter((dk) => !data?.some((doc) => doc.dateKey === dk) && dk < todayDayKey),
    [data, dateKeys, isValidating, todayDayKey]
  );

  const firstMissedKey = missedDateKeys[0];

  const generateLogGroups = useCallback(
    async (dateKey: number) => {
      const timeRange = getTimeRangeFromDateKey(dateKey);

      const documentSnapshots = await getDocs(
        query(
          collection(db, 'cafes', cafeSlug, 'storeLogs'),
          where('time', '>', timeRange[0]),
          where('time', '<', timeRange[1])
        )
      );

      const logs: StoreLog[] = [];
      documentSnapshots.forEach((doc) => logs.push(doc.data() as StoreLog));

      const archivedLogs: ArchivedStoreLog[] = logs.map(({ info, ...log }) => log);

      await setDoc<ArchivedStoreLogsGroup>(`cafes/${cafeSlug}/archivedStoreLogs/${dateKey}`, {
        dateKey,
        logs: archivedLogs,
      });
    },
    [cafeSlug]
  );

  const archivedLogs = useMemo(() => {
    if (firstMissedKey) {
      return [];
    }
    const archivedData = data?.map((i) => i.logs).flat() || [];
    if (hasToday) {
      return [...archivedData, ...(todayLogs || [])];
    }
    return archivedData;
  }, [data, firstMissedKey, hasToday, todayLogs]);

  useEffect(() => {
    if (firstMissedKey) {
      generateLogGroups(firstMissedKey);
    }
  }, [firstMissedKey, generateLogGroups]);

  return {
    logs: archivedLogs,
    isLoading: isValidating || !!firstMissedKey,
  };
};
